import React, { useEffect, useState } from "react";
import { Star, Phone, Calendar, Package, Eye, Download } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import DataTableElement from "../../components/elements/DataTableElement";
import GenericCard from "../../components/cards/GenericCard";
import CustomSelect from "../../components/form/CustomSelect";
import CustomSearchInput from "../../components/form/CustomSearchInput";
import CustomButton from "../../components/form/CustomButton";
import { get } from "../../helpers/configRoutes/api_helper";
import { currencyUSD } from "../../helpers/forms/formater";

const STATUS_OPTIONS = [
    { id: "activo",      name: "Activo (≤ 30 días)" },
    { id: "reciente",    name: "Reciente (≤ 90 días)" },
    { id: "tibio",       name: "Tibio (≤ 180 días)" },
    { id: "inactivo",    name: "Inactivo (≤ 365 días)" },
    { id: "perdido",     name: "Perdido (> 365 días)" },
    { id: "sin_compras", name: "Sin compras" },
];

const STATUS_STYLES = {
    activo:      { label: "Activo",      classes: "bg-green-100 text-green-700 border border-green-300" },
    reciente:    { label: "Reciente",    classes: "bg-emerald-100 text-emerald-700 border border-emerald-300" },
    tibio:       { label: "Tibio",       classes: "bg-yellow-100 text-yellow-700 border border-yellow-300" },
    inactivo:    { label: "Inactivo",    classes: "bg-orange-100 text-orange-700 border border-orange-300" },
    perdido:     { label: "Perdido",     classes: "bg-red-100 text-red-700 border border-red-300" },
    sin_compras: { label: "Sin compras", classes: "bg-gray-100 text-gray-600 border border-gray-300" },
};

const formatTimeAgo = (days, months) => {
    if (days === null || days === undefined) return "—";
    if (days === 0) return "Hoy";
    if (days < 30) return `Hace ${days} día${days === 1 ? "" : "s"}`;
    if (months !== null && months < 12) return `Hace ${months} mes${months === 1 ? "" : "es"}`;
    const years = Math.floor((months ?? 0) / 12);
    const restMonths = (months ?? 0) % 12;
    if (restMonths === 0) return `Hace ${years} año${years === 1 ? "" : "s"}`;
    return `Hace ${years} año${years === 1 ? "" : "s"} ${restMonths} mes${restMonths === 1 ? "" : "es"}`;
};

const StatusBadge = ({ status }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.sin_compras;
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide ${style.classes}`}>
            {style.label}
        </span>
    );
};

const ClientsFollow = () => {
    const navigate = useNavigate();
    const [tableLoad, setTableLoad] = useState({ show: true, textLoad: "Cargando seguimiento de clientes..." });
    const [loadingReport, setLoadingReport] = useState(false);
    const [filters, setFilters] = useState({
        search: "",
        status: "",
        only_top: false,
    });
    const [items, setItems] = useState([]);
    const [data, setData] = useState({
        total_clients: 0,
        top_clients: 0,
        active_clients: 0,
        inactive_clients: 0,
        no_purchases: 0,
        reference_month: "",
        compared_month: "",
    });

    useEffect(() => {
        const getItems = async () => {
            setTableLoad({ show: true, textLoad: "Cargando seguimiento de clientes..." });
            const params = {
                search: filters.search,
                status: filters.status,
                only_top: filters.only_top ? 1 : 0,
            };
            const response = await get("clients/follow", { params });
            if (response && response.status) {
                setItems(response.clients || []);
                setData(response.data || {});
            } else {
                setItems([]);
            }
            setTableLoad({ show: false, textLoad: "" });
        };
        getItems();
    }, [filters]);

    const getReport = async () => {
        setLoadingReport(true);
        const config = {
            params: {
                search: filters.search,
                status: filters.status,
                only_top: filters.only_top ? 1 : 0,
            },
            headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
            responseType: "blob",
        };
        const blob = await get("clients/follow", config);
        if (blob) {
            const url = URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Reporte seguimiento clientes.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
        setLoadingReport(false);
    };

    const columns = [
        {
            id: "cliente",
            name: "Cliente",
            selector: (row) => (
                <div className="flex items-start gap-2 py-1">
                    {row.is_top && (
                        <span
                            className="text-yellow-400 mt-0.5 flex-shrink-0"
                            data-tooltip-id={`top-${row.id}`}
                        >
                            <Star size={15} fill="currentColor" />
                            <Tooltip id={`top-${row.id}`} place="top">
                                Compró hace 1 año en este mismo mes
                            </Tooltip>
                        </span>
                    )}
                    <div className="flex flex-col gap-0.5">
                        <span className="font-semibold text-fr-dark text-sm leading-tight">{row.name}</span>
                        <span className="text-xs text-gray-500">No. {row.no}</span>
                        {row.phone && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Phone size={11} /> {row.phone}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            id: "last_purchase",
            name: "Última compra",
            selector: (row) =>
                row.last_purchase_date ? (
                    <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1 text-sm font-medium text-fr-dark">
                            <Calendar size={13} className="text-fr-primary flex-shrink-0" />
                            {row.last_purchase_date}
                        </span>
                        <span className="text-xs text-gray-500">
                            {formatTimeAgo(row.days_since_last_purchase, row.months_since_last_purchase)}
                        </span>
                    </div>
                ) : (
                    <span className="text-xs text-gray-400 italic">Sin compras</span>
                ),
        },
        {
            id: "total_purchases",
            name: "Compras",
            selector: (row) => (
                <div className="flex flex-col items-start">
                    <span className="text-lg font-bold text-fr-dark leading-none">{row.total_purchases}</span>
                    <span className="text-xs text-gray-500 mt-0.5">tickets</span>
                </div>
            ),
        },
        {
            id: "total_amount",
            name: "Importe total",
            selector: (row) => (
                <span className="text-fr-success font-semibold bg-fr-success/10 border border-fr-success/25 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                    {currencyUSD(row.total_amount ?? 0)}
                </span>
            ),
        },
        {
            id: "year_ago",
            name: "Hace 1 año (este mes)",
            selector: (row) =>
                row.is_top ? (
                    <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-1 text-sm font-medium text-fr-dark">
                            <Package size={13} className="text-fr-warning flex-shrink-0" />
                            <span className="truncate max-w-[130px]">{row.top_product_year_ago || "Producto"}</span>
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                            {currencyUSD(row.bought_year_ago_amount ?? 0)}
                        </span>
                    </div>
                ) : (
                    <span className="text-gray-400 text-sm">—</span>
                ),
        },
        {
            id: "status",
            name: "Estatus",
            selector: (row) => <StatusBadge status={row.status} />,
        },
        {
            id: "last_products",
            name: "Productos recientes",
            selector: (row) =>
                row.last_products && row.last_products.length ? (
                    <div className="flex flex-col gap-0.5 max-w-[200px]">
                        {row.last_products.slice(0, 3).map((p, i) => (
                            <span
                                key={i}
                                className={`text-xs truncate ${i === 0 ? "text-fr-dark" : "text-gray-500"}`}
                                title={`${p.name} (${p.sku})`}
                            >
                                <span className="text-fr-primary mr-1">•</span>{p.name}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-gray-400 text-sm">—</span>
                ),
        },
        {
            id: "acciones",
            name: "",
            selector: (row) => (
                <div className="flex items-center">
                    <button
                        onClick={() => navigate(`/cliente/${row.id}`)}
                        className="p-1.5 rounded-lg text-fr-primary hover:bg-fr-primary/10 hover:text-fr-primary-hover transition-all duration-150"
                        title="Ver detalle"
                        data-tooltip-id={`view-${row.id}`}
                    >
                        <Eye size={16} />
                        <Tooltip id={`view-${row.id}`} place="top">
                            Ver compras del cliente
                        </Tooltip>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="flex flex-col w-full p-5 space-y-5 bg-fr-secondary min-h-screen">

            {/* Encabezado */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-fr-dark text-2xl font-bold tracking-tight">
                        Seguimiento a Clientes
                    </h1>
                    <p className="text-gray-500 text-xs flex flex-wrap items-center gap-1.5">
                        Mes actual:
                        <strong className="text-fr-dark font-semibold">{data.reference_month || "—"}</strong>
                        <span className="text-gray-300">·</span>
                        Comparando con:
                        <strong className="text-fr-dark font-semibold">{data.compared_month || "—"}</strong>
                        <span className="text-gray-300">·</span>
                        Los marcados con{" "}
                        <Star size={11} className="inline text-yellow-400" fill="currentColor" />{" "}
                        compraron hace 1 año en este mismo mes.
                    </p>
                </div>
                <div className="flex gap-2">
                    <CustomButton
                        loading={loadingReport}
                        textLoad={"Generando reporte"}
                        onClick={getReport}
                        className="flex items-center gap-2 bg-fr-success text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition"
                    >
                        <Download size={16} />
                        Reporte Excel
                    </CustomButton>
                </div>
            </div>

            {/* Cards de métricas */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Total clientes</p>
                    <p className="text-2xl font-bold text-fr-dark">{data.total_clients ?? 0}</p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                        TOP <Star size={11} className="text-yellow-400" fill="currentColor" />
                    </p>
                    <p className="text-2xl font-bold text-fr-dark">{data.top_clients ?? 0}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Recompra esperada</p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Activos / Recientes</p>
                    <p className="text-2xl font-bold text-fr-success">{data.active_clients ?? 0}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Últimos 90 días</p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Inactivos / Perdidos</p>
                    <p className="text-2xl font-bold text-fr-danger">{data.inactive_clients ?? 0}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Más de 6 meses</p>
                </div>
                <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Sin compras</p>
                    <p className="text-2xl font-bold text-fr-dark">{data.no_purchases ?? 0}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Sin ventas registradas</p>
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-fr-light border border-gray-200 rounded-xl p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                        <div className="min-w-0">
                            <CustomSelect
                                isClearable={true}
                                className="text-sm"
                                holder="Estatus"
                                options={STATUS_OPTIONS}
                                value={filters.status}
                                handleChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-fr-dark select-none group">
                                <input
                                    type="checkbox"
                                    checked={filters.only_top}
                                    onChange={(e) =>
                                        setFilters({ ...filters, only_top: e.target.checked })
                                    }
                                    className="h-4 w-4 accent-fr-primary rounded"
                                />
                                <Star size={14} className="text-yellow-400" fill="currentColor" />
                                <span className="group-hover:text-fr-primary transition-colors duration-150">
                                    Solo TOP (recompra esperada este mes)
                                </span>
                            </label>
                        </div>
                    </div>
                    <div className="w-full lg:w-[280px]">
                        <CustomSearchInput
                            value={filters.search}
                            handleChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            holder="Buscar cliente..."
                        />
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-fr-light border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <DataTableElement columns={columns} tableLoad={tableLoad} items={items} />
            </div>

        </div>
    );
};

export default ClientsFollow;